import { injectBlockScriptsMeta } from "src/components/preview-canvas/email-preview-security";

export type PrintEmailHtmlAsPdfArgs = {
  html: string;
  title: string;
  subject?: string;
  preheader?: string;
};

/**
 * Opens the browser print dialog for the given email HTML string, allowing the
 * user to save it as a PDF. Uses a hidden iframe to avoid navigating away from
 * the current page.
 *
 * Waits for all images to finish loading before measuring content height and
 * triggering print, so the PDF matches what the user sees in the preview.
 */
function runPrintEmailHtmlAsPdf(
  { html, title, subject, preheader }: PrintEmailHtmlAsPdfArgs,
  onAfterPrint?: () => void,
): void {
  // Must be wider than common email mobile breakpoints (typically 600-640px)
  // so that mobile-only content stays hidden and doesn't inflate the PDF height.
  const EMAIL_WIDTH_PX = 660;

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = `${EMAIL_WIDTH_PX}px`;
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";
  document.body.appendChild(iframe);

  const cleanup = () => {
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  };

  const finish = () => {
    cleanup();
    onAfterPrint?.();
  };

  // If the HTML is already a full document, use it directly with print styles
  // injected. Otherwise wrap it in a minimal document shell.
  const isFullDocument = /^\s*<!doctype\s/i.test(html);

  const printStyles = `
    @media print {
      html, body {
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `;

  // Build a header block with subject/preheader if provided
  let headerHtml = "";
  if (subject || preheader) {
    const subjectLine = subject
      ? `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; color: #1a1a1a;"><span style="font-weight: 600;">Subject:</span> ${escapeHtml(subject)}</div>`
      : "";
    const preheaderLine = preheader
      ? `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; color: #6b7280; margin-top: 2px;"><span style="font-weight: 600;">Preheader:</span> ${escapeHtml(preheader)}</div>`
      : "";
    headerHtml = `<div style="padding: 12px 16px; border-bottom: 1px solid #e5e7eb; background: #fafafa;">${subjectLine}${preheaderLine}</div>`;
  }

  let docHtml: string;
  if (isFullDocument) {
    // Inject print styles into the existing <head> and prepend header after <body>
    docHtml = html.replace(/(<head[^>]*>)/i, `$1<style>${printStyles}</style>`);
    if (headerHtml) {
      docHtml = docHtml.replace(/(<body[^>]*>)/i, `$1${headerHtml}`);
    }
  } else {
    docHtml = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=${EMAIL_WIDTH_PX}">
    <title>${escapeHtml(title)}</title>
    <style>${printStyles}</style>
  </head>
  <body style="margin:0">${headerHtml}${html}</body>
</html>`;
  }

  // Use srcdoc for cleaner document loading semantics
  iframe.srcdoc = injectBlockScriptsMeta(docHtml);

  iframe.onload = () => {
    const iframeDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!iframeDoc) {
      finish();
      return;
    }

    // Wait for all images to load before measuring height and printing
    const images = Array.from(iframeDoc.querySelectorAll("img"));
    const pendingImages = images
      .filter((img) => !img.complete)
      .map(
        (img) =>
          new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          }),
      );

    void Promise.all(pendingImages).then(() => {
      const contentHeight = iframeDoc.documentElement.scrollHeight;
      const pageWidthIn = EMAIL_WIDTH_PX / 96;
      const pageHeightIn = contentHeight / 96 + 0.5;

      const pageStyle = iframeDoc.createElement("style");
      pageStyle.textContent = `@page { size: ${pageWidthIn}in ${pageHeightIn}in; margin: 0; }`;
      iframeDoc.head.appendChild(pageStyle);

      iframe.contentWindow?.addEventListener("afterprint", finish, {
        once: true,
      });
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    });
  };
}

export const printEmailHtmlAsPdf = (args: PrintEmailHtmlAsPdfArgs) => {
  runPrintEmailHtmlAsPdf(args);
};

/** Resolves after the user finishes the print dialog (including cancel), per browser `afterprint` semantics. */
export const printEmailHtmlAsPdfAsync = (
  args: PrintEmailHtmlAsPdfArgs,
): Promise<void> => {
  return new Promise((resolve) => {
    runPrintEmailHtmlAsPdf(args, resolve);
  });
};

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
